import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `sharing:` scope (F27/F28/F29/F62-64/F69/
 * F72/F73), following the exact `documents:`/`search:` precedent
 * (`apps/web/src/features/search/search.i18n.ts`) — NOT a second i18n
 * runtime (E18/§13.3 ban), just physically scoped here because
 * `packages/i18n/**` is outside this task's ownership boundary.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/sharing.json`.
 */
const en = {
  'dialog.title': 'Share “{name}”',
  'dialog.visibility.label': 'Who can access this',
  'dialog.visibility.private': 'Private',
  'dialog.visibility.private.description': 'Only you can see this. Nobody else has access.',
  'dialog.visibility.public': 'Public',
  'dialog.visibility.public.description': 'Anyone can find this on Discover, once it clears review.',
  'dialog.visibility.link': 'Anyone with the link',
  'dialog.visibility.link.description': 'Only people with the link can open this — it never appears in Discover or search.',
  'dialog.visibility.user': 'Specific person',
  'dialog.visibility.user.description': 'Invite one person as a reader or an editor.',
  'dialog.publish': 'Publish',
  'dialog.makePrivate': 'Make private',
  'dialog.save': 'Save',
  'dialog.moderation.pending.title': 'Awaiting review',
  'dialog.moderation.pending.description': 'This is queued for a quick content check before it appears on Discover. You’ll be notified the moment it’s approved.',
  'dialog.moderation.approved.title': 'Live on Discover',
  'dialog.moderation.approved.description': 'This passed review and is now public.',
  'dialog.reward.title': 'Earn credits',
  'dialog.reward.description': 'Approved public shares earn you credits automatically, and again for every reader you refer who signs up.',
  'dialog.referral.title': 'Your referral link',
  'dialog.referral.description': 'Share this exact link — when someone signs up through it, you earn credits once.',
  'dialog.link.title': 'Shareable link',
  'dialog.link.generate': 'Create link',
  'dialog.link.copy': 'Copy link',
  'dialog.link.copied': 'Copied',
  'dialog.link.revoke': 'Revoke link',
  'dialog.link.revoked.notice': 'This link has been revoked. Anyone still holding it can no longer open this.',
  'dialog.link.expiry.label': 'Link expires',
  'dialog.link.expiry.never': 'Never',
  'dialog.grant.userIdLabel': 'User ID',
  'dialog.grant.userIdHint': 'Paste the exact account ID to invite — a name/email lookup isn’t available yet.',
  'dialog.grant.roleLabel': 'Role',
  'dialog.grant.role.reader': 'Reader — can read only',
  'dialog.grant.role.editor': 'Editor — can read and edit',
  'dialog.grant.add': 'Add person',
  'dialog.grant.remove': 'Remove access',
  'dialog.grant.empty': 'Nobody has been invited yet.',
  'dialog.policy.title': 'This didn’t clear review',
  'dialog.policy.retry': 'Edit and try again',
  'dialog.policy.terms': 'Read the policy',
  'toast.published.action': 'Published',
  'toast.privated.action': 'Made private',
  'toast.linkIssued.action': 'Link created',
  'toast.linkRevoked.action': 'Link revoked',
  'toast.grantAdded.action': 'Invited',
  'toast.grantRemoved.action': 'Access removed',
  'toast.publishFailed': 'Couldn’t update sharing — try again.',
  'reaction.like': 'Like',
  'reaction.dislike': 'Dislike',
  'reaction.liked': 'You liked this',
  'reaction.disliked': 'You disliked this',
  'reaction.signInToReact': 'Sign in to react',
  'reaction.setFailed': 'Couldn’t save your reaction — try again.',
  'comments.title': '{count, plural, one {Comment} other {Comments}} · {count}',
  'comments.composer.placeholder': 'Add a comment…',
  'comments.composer.post': 'Post',
  'comments.composer.signInToComment': 'Sign in to comment',
  'comments.reply': 'Reply',
  'comments.replyPlaceholder': 'Write a reply…',
  'comments.viewReplies': '{count, plural, one {View # reply} other {View # replies}}',
  'comments.hideReplies': 'Hide replies',
  'comments.delete': 'Delete',
  'comments.deleted': 'This comment was removed.',
  'comments.removedByModerator': 'This comment was removed by a moderator.',
  'comments.badge.pro': 'PRO',
  'comments.likeCount': '{count, plural, one {# like} other {# likes}}',
  'comments.dislikeCount': '{count, plural, one {# dislike} other {# dislikes}}',
  'comments.empty.title': 'No comments yet',
  'comments.empty.description': 'Be the first to say something.',
  'comments.error.title': 'Couldn’t load comments.',
  'comments.postFailed': 'Couldn’t post your comment — try again.',
  'comments.deleteFailed': 'Couldn’t delete this comment — try again.',
  'moderator.badge': 'MODERATOR',
  'moderator.viewingAs': 'You’re viewing this as staff.',
  'reader.readFull': 'Read',
  'moderator.takedown': 'Take down',
  'moderator.takedown.title': 'Take down',
  'moderator.takedown.description': '“{title}” will be removed and the owner notified. This is recorded in the audit log.',
  'moderator.takedown.reasonLabel': 'Policy reason',
  'moderator.takedown.reasonPlaceholder': 'Choose a reason',
  'moderator.takedown.noteLabel': 'Note for the audit log (optional)',
  'moderator.takedown.confirm': 'Remove content',
  'moderator.takedown.failed': 'Couldn’t remove this — try again.',
  'moderator.takedown.reasonRequired': 'Choose a policy reason.',
  'policyCategory.hate': 'Hate',
  'policyCategory.harassment': 'Harassment',
  'policyCategory.sexual': 'Sexual content',
  'policyCategory.sexual_minors': 'Sexual content involving minors',
  'policyCategory.violence': 'Violence',
  'policyCategory.self_harm': 'Self-harm',
  'policyCategory.illegal': 'Illegal activity',
  'policyCategory.malware': 'Malware',
  'policyCategory.pii': 'Personal information',
} as const;

const ro: Record<keyof typeof en, string> = {
  'dialog.title': 'Distribuie „{name}”',
  'dialog.visibility.label': 'Cine poate accesa asta',
  'dialog.visibility.private': 'Privat',
  'dialog.visibility.private.description': 'Doar tu poți vedea asta. Nimeni altcineva nu are acces.',
  'dialog.visibility.public': 'Public',
  'dialog.visibility.public.description': 'Oricine poate găsi asta pe Descoperă, după ce trece de verificare.',
  'dialog.visibility.link': 'Oricine are linkul',
  'dialog.visibility.link.description': 'Doar cei cu linkul pot deschide asta — nu apare niciodată pe Descoperă sau în căutare.',
  'dialog.visibility.user': 'O persoană anume',
  'dialog.visibility.user.description': 'Invită o singură persoană ca cititor sau editor.',
  'dialog.publish': 'Publică',
  'dialog.makePrivate': 'Fă privat',
  'dialog.save': 'Salvează',
  'dialog.moderation.pending.title': 'În așteptarea verificării',
  'dialog.moderation.pending.description': 'Asta este în coadă pentru o verificare rapidă a conținutului înainte de a apărea pe Descoperă. Vei fi anunțat imediat ce este aprobat.',
  'dialog.moderation.approved.title': 'Live pe Descoperă',
  'dialog.moderation.approved.description': 'Asta a trecut verificarea și este acum public.',
  'dialog.reward.title': 'Câștigă credite',
  'dialog.reward.description': 'Distribuirile publice aprobate îți aduc credite automat, și din nou pentru fiecare cititor pe care îl aduci și care se înscrie.',
  'dialog.referral.title': 'Linkul tău de recomandare',
  'dialog.referral.description': 'Distribuie exact acest link — când cineva se înscrie prin el, câștigi credite o singură dată.',
  'dialog.link.title': 'Link de distribuire',
  'dialog.link.generate': 'Creează link',
  'dialog.link.copy': 'Copiază linkul',
  'dialog.link.copied': 'Copiat',
  'dialog.link.revoke': 'Revocă linkul',
  'dialog.link.revoked.notice': 'Acest link a fost revocat. Cei care îl mai au nu mai pot deschide asta.',
  'dialog.link.expiry.label': 'Linkul expiră',
  'dialog.link.expiry.never': 'Niciodată',
  'dialog.grant.userIdLabel': 'ID utilizator',
  'dialog.grant.userIdHint': 'Lipește ID-ul exact al contului de invitat — căutarea după nume/email nu este încă disponibilă.',
  'dialog.grant.roleLabel': 'Rol',
  'dialog.grant.role.reader': 'Cititor — poate doar citi',
  'dialog.grant.role.editor': 'Editor — poate citi și edita',
  'dialog.grant.add': 'Adaugă persoană',
  'dialog.grant.remove': 'Elimină accesul',
  'dialog.grant.empty': 'Nimeni nu a fost invitat încă.',
  'dialog.policy.title': 'Asta nu a trecut verificarea',
  'dialog.policy.retry': 'Editează și încearcă din nou',
  'dialog.policy.terms': 'Citește politica',
  'toast.published.action': 'Publicat',
  'toast.privated.action': 'Făcut privat',
  'toast.linkIssued.action': 'Link creat',
  'toast.linkRevoked.action': 'Link revocat',
  'toast.grantAdded.action': 'Invitat',
  'toast.grantRemoved.action': 'Acces eliminat',
  'toast.publishFailed': 'Nu am putut actualiza distribuirea — încearcă din nou.',
  'reaction.like': 'Apreciază',
  'reaction.dislike': 'Nu apreciază',
  'reaction.liked': 'Ai apreciat asta',
  'reaction.disliked': 'Nu ai apreciat asta',
  'reaction.signInToReact': 'Autentifică-te pentru a reacționa',
  'reaction.setFailed': 'Nu am putut salva reacția — încearcă din nou.',
  'comments.title': '{count, plural, one {Comentariu} few {Comentarii} other {Comentarii}} · {count}',
  'comments.composer.placeholder': 'Adaugă un comentariu…',
  'comments.composer.post': 'Postează',
  'comments.composer.signInToComment': 'Autentifică-te pentru a comenta',
  'comments.reply': 'Răspunde',
  'comments.replyPlaceholder': 'Scrie un răspuns…',
  'comments.viewReplies': '{count, plural, one {Vezi # răspuns} few {Vezi # răspunsuri} other {Vezi # de răspunsuri}}',
  'comments.hideReplies': 'Ascunde răspunsurile',
  'comments.delete': 'Șterge',
  'comments.deleted': 'Acest comentariu a fost eliminat.',
  'comments.removedByModerator': 'Acest comentariu a fost eliminat de un moderator.',
  'comments.badge.pro': 'PRO',
  'comments.likeCount': '{count, plural, one {# apreciere} few {# aprecieri} other {# de aprecieri}}',
  'comments.dislikeCount': '{count, plural, one {# neplăcere} few {# neplăceri} other {# de neplăceri}}',
  'comments.empty.title': 'Niciun comentariu încă',
  'comments.empty.description': 'Fii primul care spune ceva.',
  'comments.error.title': 'Nu am putut încărca comentariile.',
  'comments.postFailed': 'Nu am putut posta comentariul — încearcă din nou.',
  'comments.deleteFailed': 'Nu am putut șterge comentariul — încearcă din nou.',
  'moderator.badge': 'MODERATOR',
  'moderator.viewingAs': 'Vizualizezi asta ca membru al echipei.',
  'reader.readFull': 'Citește',
  'moderator.takedown': 'Elimină',
  'moderator.takedown.title': 'Elimină conținutul',
  'moderator.takedown.description': '„{title}” va fi eliminat, iar proprietarul va fi anunțat. Asta este înregistrat în jurnalul de audit.',
  'moderator.takedown.reasonLabel': 'Motiv de politică',
  'moderator.takedown.reasonPlaceholder': 'Alege un motiv',
  'moderator.takedown.noteLabel': 'Notă pentru jurnalul de audit (opțional)',
  'moderator.takedown.confirm': 'Elimină conținutul',
  'moderator.takedown.failed': 'Nu am putut elimina asta — încearcă din nou.',
  'moderator.takedown.reasonRequired': 'Alege un motiv de politică.',
  'policyCategory.hate': 'Ură',
  'policyCategory.harassment': 'Hărțuire',
  'policyCategory.sexual': 'Conținut sexual',
  'policyCategory.sexual_minors': 'Conținut sexual cu minori',
  'policyCategory.violence': 'Violență',
  'policyCategory.self_harm': 'Auto-vătămare',
  'policyCategory.illegal': 'Activitate ilegală',
  'policyCategory.malware': 'Malware',
  'policyCategory.pii': 'Date personale',
};

export const SHARING_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type SharingCatalogKey = keyof typeof en;
